import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  FileText,
  Loader2,
  ReceiptText,
  RefreshCw,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ContactUsMenu } from '@/components/layout/ContactUsMenu';
import { useCustomer } from '@/hooks/useCustomer';
import { useInvoices, type InvoiceRow } from '@/hooks/useInvoices';
import { usePendingPayments, processingNote, type PendingPayment } from '@/hooks/usePendingPayments';
import { RecurringRentalModal } from '@/components/RecurringRentalModal';
import { fmtMirrorDate } from '@/lib/dates';
import { useGroupInvoices } from '@/hooks/useGroupInvoices';
import BalanceWithCredits from '@/components/billing/BalanceWithCredits';
import ChainInvoices from '@/pages/invoices/ChainInvoices';

type Filter = 'open' | 'pastdue' | 'paid' | 'all';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'pastdue', label: 'Past due' },
  { id: 'paid', label: 'Paid' },
  { id: 'all', label: 'All' },
];

function fmtDate(iso: string): string {
  return fmtMirrorDate(iso);
}

// Payments run EXCLUSIVELY through our /pay page (Stripe rail). The QBO
// hosted "Pay now" link (invoice_payment_url) is deliberately no longer
// offered anywhere — Sky 2026-07-22: no other payment service allowed.

interface RecurringTarget {
  kind: 'tank' | 'equipment';
  invoiceId: string;
  amount: number;
  alreadyOn: boolean;
}

export default function Invoices() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('open');
  const { data: invoices, isLoading } = useInvoices();
  const { data: ctx } = useCustomer();
  const { data: pendingPayments } = usePendingPayments();
  const [recurring, setRecurring] = useState<RecurringTarget | null>(null);

  // A chain's accounting login standing on the master gets EVERY store's
  // invoices instead of the master account's own handful. Everyone else —
  // including a store manager and the same person once they pick a store —
  // gets the page below unchanged.
  const { data: group, isLoading: groupLoading } = useGroupInvoices();

  // Money of theirs we are holding. Above the summary cards on purpose: it is
  // the one thing on this page that needs an answer FROM them, and it is not a
  // debt, so it must not be buried under what they owe.

  const filtered = useMemo(() => {
    const rows = invoices ?? [];
    if (filter === 'open') return rows.filter((r) => r.isOpen);
    if (filter === 'pastdue') return rows.filter((r) => r.isOpen && (r.daysPastDue ?? 0) > 0);
    if (filter === 'paid') return rows.filter((r) => !r.isOpen);
    return rows;
  }, [invoices, filter]);

  const totals = useMemo(() => {
    const all = invoices ?? [];
    const open = all.filter((r) => r.isOpen);
    return {
      open: open.reduce((s, r) => s + r.balance, 0),
      pastDue: open.filter((r) => (r.daysPastDue ?? 0) > 0).reduce((s, r) => s + r.balance, 0),
      openCount: open.length,
      paidCount: all.length - open.length,
    };
  }, [invoices]);

  if (group?.is_group) return <ChainInvoices />;

  // Hold the single-account render until we know which page this is — a flash
  // of the master's own seven invoices before the chain list lands reads as
  // "where did everything go".
  if (groupLoading) {
    return (
      <div className="px-6 py-16 text-center text-sm text-brix-muted">
        <Loader2 size={16} className="animate-spin inline-block mr-2" />
        Loading your invoices…
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-thin pb-16 md:pb-10">
      <div className="px-6 md:px-8 pt-8 pb-12 max-w-5xl mx-auto">
        <h1 className="mb-4 text-center font-display text-2xl font-bold tracking-tight text-brix-ink">
          Invoices
        </h1>
        <p className="text-center text-base mb-7 text-brix-muted max-w-2xl mx-auto">
          Up-to-date invoices for your account. Pay one, pay them all at once, or put a rental on autopay.
        </p>

        {/* Each card filters the table below to its slice. */}
        <div data-tour="inv-summary" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            label="Open balance"
            value={formatUsd(totals.open)}
            active={filter === 'open'}
            onClick={() => setFilter('open')}
          />
          <SummaryCard
            label="Past due"
            value={formatUsd(totals.pastDue)}
            tone={totals.pastDue > 0 ? 'warning' : 'muted'}
            active={filter === 'pastdue'}
            onClick={() => setFilter('pastdue')}
          />
          <SummaryCard
            label="Open invoices"
            value={isLoading ? '—' : String(totals.openCount)}
            active={filter === 'open'}
            onClick={() => setFilter('open')}
          />
          <SummaryCard
            label="Paid invoices"
            value={isLoading ? '—' : String(totals.paidCount)}
            active={filter === 'paid'}
            onClick={() => setFilter('paid')}
          />
        </div>

        <BalanceWithCredits openBalance={totals.open} />

        {/* Filters + Pay multiple */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mb-5">
          <div data-tour="inv-filters" className="flex justify-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={cn(
                  'px-5 py-2 text-sm rounded-full cursor-pointer transition-all duration-200',
                  filter === f.id
                    ? 'bg-brix-primary-tint text-brix-primary font-semibold shadow-card'
                    : 'text-brix-muted hover:bg-brix-primary-tint/60 hover:text-brix-ink',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div data-tour="inv-tools" className="flex items-center gap-2">
            <Link
              to="/statements"
              title="View your account statement (open items + aging)"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-brix-primary/40 text-brix-primary text-sm font-semibold hover:bg-brix-primary-tint/40 transition-colors"
            >
              <ReceiptText size={15} /> View statement
            </Link>
            <Link
              to="/autopay"
              title="Save a card and put rentals on automatic payment"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-brix-primary/40 text-brix-primary text-sm font-semibold hover:bg-brix-primary-tint/40 transition-colors"
            >
              <RefreshCw size={15} /> Autopay
            </Link>
            {totals.open > 0 && (
              <Link
                to="/pay"
                data-tour="inv-paymultiple"
                title="Select and pay multiple invoices together"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold shadow-card hover:bg-emerald-700 transition-colors"
              >
                <CreditCard size={15} /> Pay multiple invoices
              </Link>
            )}
          </div>
        </div>

        <div data-tour="inv-list" className="rounded-2xl border-2 border-brix-primary/90 bg-brix-surface/85 backdrop-blur-xl overflow-hidden shadow-card-deep">
          <div className="h-1.5 bg-brix-primary" aria-hidden />
          <div className="hidden sm:grid grid-cols-[1fr_110px_120px_130px_150px_28px] gap-4 px-5 py-3.5 text-xs font-semibold tracking-tight text-brix-muted border-b border-brix-border bg-brix-surface-alt/60 items-center">
            <div>Invoice</div>
            <div>Date</div>
            <div>Due</div>
            <div className="text-right">Balance</div>
            <div className="text-right">Pay</div>
            <div />
          </div>

          {isLoading ? (
            <div className="px-4 py-14 text-center text-sm text-brix-muted">
              <Loader2 size={18} className="animate-spin inline-block mr-2" /> Loading invoices…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-14 text-center text-sm text-brix-muted">
              {filter === 'open'
                ? 'No open invoices. Nice.'
                : filter === 'pastdue'
                  ? 'Nothing past due. Nice.'
                  : filter === 'paid'
                    ? 'No paid invoices yet.'
                    : 'No invoices on file yet.'}
            </div>
          ) : (
            filtered.map((row) => (
              <InvoiceListRow
                key={row.qboInvoiceId}
                row={row}
                processing={pendingPayments?.get(row.qboInvoiceId) ?? null}
                onPay={() => navigate(`/pay?invoice=${encodeURIComponent(row.qboInvoiceId)}`)}
                onRecurring={() =>
                  row.rentalKind &&
                  setRecurring({ kind: row.rentalKind, invoiceId: row.qboInvoiceId, amount: row.balance || row.totalAmount, alreadyOn: row.rentalAutopay })
                }
              />
            ))
          )}
        </div>

        <div className="text-center mt-8 space-y-3">
          <p className="text-sm text-brix-muted">Questions about a charge?</p>
          <ContactUsMenu variant="inline" triggerLabel="Chat with us" initialChannel="email" />
        </div>
      </div>

      {recurring && ctx && (
        <RecurringRentalModal
          customerId={ctx.customer.id}
          rentalKind={recurring.kind}
          sourceInvoiceId={recurring.invoiceId}
          amount={recurring.amount}
          alreadyOn={recurring.alreadyOn}
          onClose={() => setRecurring(null)}
        />
      )}
    </div>
  );
}

function InvoiceListRow({
  row,
  processing,
  onPay,
  onRecurring,
}: {
  row: InvoiceRow;
  processing: PendingPayment | null;
  onPay: () => void;
  onRecurring: () => void;
}) {
  const isPaid = !row.isOpen;
  const isPastDue = row.isOpen && (row.daysPastDue ?? 0) > 0;
  const balanceColor = isPaid ? 'text-emerald-700' : isPastDue ? 'text-red-600' : 'text-slate-900';
  // A rental invoice whose kind is on autopay is shown grayed + "On auto pay".
  // ⚠ Only a SETTLED autopay rental is dimmed. Dimming an open one made an
  //   unpaid balance read as handled — see InvoiceDetail's PayBlock.
  const onAutopay = row.isRental && row.rentalAutopay && !row.isOpen;
  const to = `/invoices/${encodeURIComponent(row.qboInvoiceId)}`;
  const dim = onAutopay ? 'opacity-60 hover:bg-brix-surface-alt/40' : 'hover:bg-brix-primary-tint/30';

  // Invoice identity block (icon + number + memo) — shared by both layouts.
  const head = (
    <div className="flex items-center gap-3 min-w-0">
      <div
        className={cn(
          'w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0 shadow-card',
          isPaid ? 'bg-emerald-50 text-emerald-700' : isPastDue ? 'bg-red-50 text-red-600' : 'bg-brix-primary-tint text-brix-primary',
        )}
      >
        {isPaid ? <CheckCircle2 size={18} /> : <FileText size={18} />}
      </div>
      <div className="min-w-0">
        <div className={cn('text-base sm:text-lg font-bold font-mono leading-tight tracking-tight flex items-center gap-2 flex-wrap', balanceColor)} style={{ fontVariantNumeric: 'tabular-nums' }}>
          #{row.docNumber ?? row.qboInvoiceId}
          {row.isRental && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-tight bg-brix-primary-tint text-brix-primary">
              <CalendarClock size={9} /> {row.rentalKind === 'equipment' ? 'Equipment' : 'Cylinder'} rental
            </span>
          )}
        </div>
        {row.memo && <div className="text-xs text-slate-500 truncate max-w-[13rem] sm:max-w-[26rem] mt-0.5">{row.memo}</div>}
      </div>
    </div>
  );

  // Balance block — shared.
  const balance = (
    <div className="text-right">
      <div className={cn('font-mono text-lg sm:text-xl font-bold leading-tight tracking-tight', balanceColor)} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatUsd(isPaid ? row.totalAmount : row.balance)}
      </div>
      {!isPaid && row.balance !== row.totalAmount && (
        <div className="font-mono text-xs text-slate-500 mt-0.5">of {formatUsd(row.totalAmount)}</div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile: stacked card (the fixed desktop grid overflows narrow screens). */}
      <Link to={to} className={cn('flex sm:hidden items-start gap-3 px-4 py-3.5 border-b border-brix-border last:border-b-0 transition-colors', dim)}>
        <div className="min-w-0 flex-1">
          {head}
          <div className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1.5 flex-wrap">
            <span>{fmtDate(row.txnDate)}</span>
            <span aria-hidden>·</span>
            <span>Due {fmtDate(row.dueDate)}</span>
          </div>
          <StatusLabel row={row} processing={processing} />
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {balance}
          <PayActions row={row} processing={processing} onPay={onPay} onRecurring={onRecurring} />
        </div>
      </Link>

      {/* Desktop: aligned table row. */}
      <Link to={to} className={cn('hidden sm:grid grid-cols-[1fr_110px_120px_130px_150px_28px] gap-4 px-5 py-4 items-center border-b border-brix-border last:border-b-0 transition-colors cursor-pointer', dim)}>
        {head}
        <div className="text-sm text-slate-900 font-medium">{fmtDate(row.txnDate)}</div>
        <div className="text-sm">
          <div className="text-slate-900 font-medium">{fmtDate(row.dueDate)}</div>
          <StatusLabel row={row} processing={processing} />
        </div>
        {balance}
        <PayActions row={row} processing={processing} onPay={onPay} onRecurring={onRecurring} />
        <ChevronRight size={16} className="text-brix-muted" />
      </Link>
    </>
  );
}

function PayActions({
  row,
  processing,
  onPay,
  onRecurring,
}: {
  row: InvoiceRow;
  processing: PendingPayment | null;
  onPay: () => void;
  onRecurring: () => void;
}) {
  const isPaid = !row.isOpen;
  // Enrolled AND settled — the chip replaces the Pay button only then. An open
  // invoice always keeps a way to pay it, whatever autopay claims.
  const onAutopay = row.isRental && row.rentalAutopay && !row.isOpen;
  const enrolledButOpen = row.isRental && row.rentalAutopay && row.isOpen;
  // A payment we initiated is settling — show "Processing" so they don't pay twice.
  if (processing && !isPaid) {
    return (
      <StatusBadge
        tone="warning"
        size="sm"
        icon={<Loader2 size={11} className="animate-spin" />}
        title={processingNote(processing.bank)}
      >
        {processing.bank ? 'Clearing · 3–5 days' : 'Processing'}
      </StatusBadge>
    );
  }
  return (
    <div className="flex flex-col items-end gap-1.5" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      {row.isRental ? (
        onAutopay ? (
          <button
            type="button"
            onClick={onRecurring}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold tracking-tight bg-brix-surface-alt text-brix-muted border border-brix-border hover:bg-brix-primary-tint/40 cursor-pointer"
          >
            <RefreshCw size={11} /> On auto pay
          </button>
        ) : (
          <>
            {enrolledButOpen && (
              <button
                type="button"
                onClick={onRecurring}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-tight bg-brix-surface-alt text-brix-muted border border-brix-border hover:bg-brix-primary-tint/40 cursor-pointer"
              >
                <RefreshCw size={10} /> On auto pay — not charged yet
              </button>
            )}
            {row.isOpen && (
              <button
                type="button"
                onClick={onPay}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold tracking-tight bg-emerald-600 text-white border border-emerald-700 hover:bg-emerald-700 cursor-pointer shadow-card"
              >
                <CreditCard size={11} /> Pay invoice
              </button>
            )}
            <button
              type="button"
              onClick={onRecurring}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-tight bg-brix-primary-tint text-brix-primary border border-brix-primary/30 hover:bg-brix-primary-tint/70 cursor-pointer"
            >
              <CalendarClock size={11} /> Set up recurring
            </button>
          </>
        )
      ) : isPaid ? (
        <StatusBadge tone="success" size="sm" icon={<CheckCircle2 size={11} />}>
          Paid
        </StatusBadge>
      ) : row.isOpen ? (
        <button
          type="button"
          onClick={onPay}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold tracking-tight bg-emerald-600 text-white border border-emerald-700 hover:bg-emerald-700 cursor-pointer shadow-card"
        >
          <CreditCard size={11} /> Pay invoice
        </button>
      ) : (
        <StatusBadge tone="neutral" size="sm" title="Online payment is being switched on">
          Pay online soon
        </StatusBadge>
      )}
    </div>
  );
}

function StatusLabel({ row, processing }: { row: InvoiceRow; processing?: PendingPayment | null }) {
  if (processing && row.isOpen) {
    return (
      <span className="inline-block mt-0.5 text-xs font-semibold tracking-tight text-amber-700">
        {processing.bank ? 'Bank payment clearing — 3–5 business days' : 'Payment processing'}
      </span>
    );
  }
  if (!row.isOpen) {
    return (
      <span className="inline-flex items-center gap-1 mt-0.5 text-xs font-semibold tracking-tight text-emerald-700">
        <CheckCircle2 size={10} /> Paid
      </span>
    );
  }
  if ((row.daysPastDue ?? 0) > 0) {
    const days = row.daysPastDue ?? 0;
    return (
      <span className="inline-block mt-0.5 text-xs font-semibold tracking-tight text-red-600">
        Past due · {days} day{days === 1 ? '' : 's'}
      </span>
    );
  }
  return <span className="inline-block mt-0.5 text-xs font-semibold tracking-tight text-brix-primary">Unpaid</span>;
}

function SummaryCard({
  label,
  value,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: string;
  tone?: 'warning' | 'muted';
  active?: boolean;
  onClick?: () => void;
}) {
  const valueColor = tone === 'warning' ? 'text-red-600' : tone === 'muted' ? 'text-slate-500' : 'text-slate-900';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={`Show these invoices in the table below`}
      className={cn(
        'rounded-2xl border bg-brix-surface/85 backdrop-blur-xl px-5 py-5 shadow-card-hover hover:shadow-card-deep hover:-translate-y-0.5 transition-all duration-300 flex flex-col items-center justify-center text-center h-full cursor-pointer',
        active ? 'border-2 border-brix-primary' : 'border-brix-border',
      )}
    >
      <div className="text-xs tracking-tight text-slate-500 font-semibold">{label}</div>
      <div className={cn('mt-2 font-mono font-bold text-3xl md:text-4xl leading-none tracking-tight', valueColor)} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
    </button>
  );
}
